import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios, { getApiUrl } from "@/lib/api";
import {
  Users, Gamepad2, Swords, Trophy, Target, BookOpen, Heart, Ban,
  Plus, Trash2, Eye, EyeOff, MessageSquare, Send, Search, Filter,
  Loader2, X, UserCheck, Clock, Pause, Play, Calendar, Globe, Wifi, WifiOff,
  Edit2, Check, Shield, Baby, IdCard, Sparkles, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import PlatformBadge from "@/components/PlatformBadge";
import CrossPlaySuggestionDialog from "@/components/Multiplayer/CrossPlaySuggestionDialog";

const API = `${getApiUrl()}/api`;

const CATEGORIES = {
  coop: { label: "Co-op", icon: Users, color: "#3B82F6", bg: "bg-blue-500/15 text-blue-400 border-blue-500/30", isMatchType: true },
  versus: { label: "Versus", icon: Swords, color: "#F43F5E", bg: "bg-rose-500/15 text-rose-400 border-rose-500/30", isMatchType: true },
  ranked: { label: "Ranked", icon: Trophy, color: "#F59E0B", bg: "bg-amber-500/15 text-amber-400 border-amber-500/30", isMatchType: true },
  social: { label: "Casual", icon: Heart, color: "#10B981", bg: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", isMatchType: true },
  custom: { label: "Custom", icon: Sparkles, color: "#06B6D4", bg: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30", isMatchType: true },
  boost: { label: "Boost", icon: Target, color: "#EF4444", bg: "bg-red-500/15 text-red-400 border-red-500/30" },
  backlog_willing: { label: "Backlog but Willing", icon: BookOpen, color: "#8B5CF6", bg: "bg-violet-500/15 text-violet-400 border-violet-500/30" },
  completed_willing: { label: "Completed, Willing to Help", icon: Heart, color: "#EC4899", bg: "bg-pink-500/15 text-pink-400 border-pink-500/30" },
};

const MATCH_TYPE_KEYS = ["coop", "versus", "ranked", "social", "custom"];

const MP_TYPES = {
  full: { label: "Full MP", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
  local_only: { label: "Local Only", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  limited: { label: "Limited MP", color: "text-orange-400 bg-orange-500/10 border-orange-500/20" },
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = Array.from({ length: 48 }, (_, i) => i * 0.5); // 0, 0.5, 1, ... 23.5

function formatHour(h) {
  const hour = Math.floor(h);
  const min = h % 1 === 0.5 ? "30" : "00";
  const ampm = hour >= 12 ? "PM" : "AM";
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${display}:${min} ${ampm}`;
}

// ===== Kid-Friendly mature-rating warning =====
// Detects ESRB Teen/Mature/Adults-Only or PEGI 16/18 strings on a game's ratings array.
const KIDSAFE_ACK_KEY = "mp_kidsafe_mature_ack";

function isMatureRating(ratings) {
  if (!Array.isArray(ratings) || ratings.length === 0) return false;
  return ratings.some(r => {
    const s = String(r).toUpperCase();
    if (/\b(T|M|AO)\s*\(\s*ESRB\s*\)/.test(s)) return true;
    if (/\(\s*PEGI\s*\)/.test(s) && /\b(12|16|18)\b/.test(s)) return true;
    return false;
  });
}

function matureRatingLabels(ratings) {
  return (ratings || []).filter(r => {
    const s = String(r).toUpperCase();
    if (/\b(T|M|AO)\s*\(\s*ESRB\s*\)/.test(s)) return true;
    if (/\(\s*PEGI\s*\)/.test(s) && /\b(12|16|18)\b/.test(s)) return true;
    return false;
  });
}

function KidSafeMatureWarningDialog({ open, gameName, ratings, onConfirm, onCancel }) {
  const [silenceSession, setSilenceSession] = useState(false);
  useEffect(() => { if (open) setSilenceSession(false); }, [open]);
  const labels = matureRatingLabels(ratings);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <DialogContent data-testid="mp-kidsafe-warning" className="bg-[#0f0f0f] border-amber-500/30 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Mature-rated game — please confirm
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-white/80">
            <span className="font-medium text-white">{gameName}</span> is rated{" "}
            {labels.length > 0 ? (
              <span className="text-amber-300">{labels.join(", ")}</span>
            ) : (
              <span className="text-amber-300">Teen/Mature</span>
            )}.
          </p>
          <p className="text-white/60 leading-relaxed">
            By marking it <span className="text-emerald-300">OK to play during Kid-Friendly hours</span>, you confirm
            you are comfortable playing this title in the presence of children. Content ratings (ESRB, PEGI, etc.)
            are guidelines designed to help shield young players from violent or sexual content — but the final
            decision about what your children are exposed to rests with you, the parent or guardian.
          </p>
          <p className="text-white/40 text-xs italic">
            Game-o-Rang does not police what you play. Kid-Friendly hours are a self-reported coordination tool, not a content filter.
          </p>
          <label className="flex items-center gap-2 pt-2 cursor-pointer">
            <input
              data-testid="mp-kidsafe-warning-silence"
              type="checkbox"
              checked={silenceSession}
              onChange={(e) => setSilenceSession(e.target.checked)}
              className="accent-amber-500"
            />
            <span className="text-white/60 text-xs">Don't show this again until I sign in next time</span>
          </label>
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="mp-kidsafe-warning-cancel"
              variant="outline"
              onClick={() => onCancel?.()}
              className="flex-1 bg-white/5 border-white/10 text-white/70 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              data-testid="mp-kidsafe-warning-confirm"
              onClick={() => {
                if (silenceSession) {
                  try { sessionStorage.setItem(KIDSAFE_ACK_KEY, "1"); } catch {}
                }
                onConfirm?.();
              }}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            >
              I understand
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function shouldShowKidSafeWarning(ratings) {
  if (!isMatureRating(ratings)) return false;
  try {
    return sessionStorage.getItem(KIDSAFE_ACK_KEY) !== "1";
  } catch {
    return true;
  }
}

function CategoryBadge({ category, size = "sm", count }) {
  const cat = CATEGORIES[category];
  if (!cat) return null;
  const Icon = cat.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cat.bg}`}>
      <Icon className={size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5"} />
      {size !== "xs" && cat.label}
      {count != null && <span className="ml-0.5 opacity-70">({count})</span>}
    </span>
  );
}

function MpTypeBadge({ type }) {
  const t = MP_TYPES[type];
  if (!t) return null;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${t.color}`}>{t.label}</span>;
}


// ===== AVAILABILITY TIME BAR =====
// State machine: cell value is one of:
//   "none"  | "kid_available" | "adult_available" | "kid_flexible" | "adult_flexible"
// Click cycle within current paint mode (kid|adult):
//   none -> <mode>_available -> <mode>_flexible -> none
// If cell is in opposite mode, paint replaces it with <mode>_available.
function AvailabilityBar({ daySlots, onUpdate, day, paintMode }) {
  const barRef = useRef(null);
  const [dragging, setDragging] = useState(null); // { newState, start: hourIndex }

  const grid = useMemo(() => {
    const cells = Array(48).fill("none");
    for (const slot of daySlots) {
      const startIdx = Math.round(slot.start_hour * 2);
      const endIdx = Math.round(slot.end_hour * 2);
      const kid = !!slot.kid_friendly;
      const value = `${kid ? "kid" : "adult"}_${slot.flexibility}`;
      for (let i = startIdx; i < endIdx && i < 48; i++) {
        cells[i] = value;
      }
    }
    return cells;
  }, [daySlots]);

  const getHourFromEvent = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    return Math.round((x / rect.width) * 48) * 0.5;
  };

  const computeNextState = (current) => {
    const want = paintMode; // "kid" | "adult"
    const other = want === "kid" ? "adult" : "kid";
    if (current === `${other}_available` || current === `${other}_flexible`) {
      return `${want}_available`; // replace opposite mode with available in current mode
    }
    if (current === "none") return `${want}_available`;
    if (current === `${want}_available`) return `${want}_flexible`;
    return "none"; // from <want>_flexible
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    const hour = getHourFromEvent(e);
    const idx = Math.round(hour * 2);
    const newState = computeNextState(grid[idx]);
    setDragging({ newState, start: hour });
    updateSlot(hour, hour + 0.5, newState);
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    const hour = getHourFromEvent(e);
    const min = Math.min(dragging.start, hour);
    const max = Math.max(dragging.start, hour + 0.5);
    updateSlot(min, max, dragging.newState);
  };

  const handleMouseUp = () => setDragging(null);

  const stateToSlot = (state, day, start, end) => {
    if (state === "none") return null;
    const [mode, flex] = state.split("_");
    return { day, start_hour: start, end_hour: end, flexibility: flex, kid_friendly: mode === "kid" };
  };

  const updateSlot = (startHour, endHour, state) => {
    const newCells = [...grid];
    const startIdx = Math.round(startHour * 2);
    const endIdx = Math.round(endHour * 2);
    for (let i = startIdx; i < endIdx && i < 48; i++) {
      newCells[i] = state;
    }
    // Convert cells back to slots (merge consecutive same-state cells)
    const newSlots = [];
    let i = 0;
    while (i < 48) {
      if (newCells[i] !== "none") {
        const s = newCells[i];
        let j = i;
        while (j < 48 && newCells[j] === s) j++;
        const slot = stateToSlot(s, day, i * 0.5, j * 0.5);
        if (slot) newSlots.push(slot);
        i = j;
      } else {
        i++;
      }
    }
    onUpdate(newSlots);
  };

  const cellColor = (state) => {
    switch (state) {
      case "kid_available": return "bg-emerald-500/70";
      case "adult_available": return "bg-amber-500/70";
      case "kid_flexible": return "bg-sky-500/55";
      case "adult_flexible": return "bg-orange-500/55";
      default: return "";
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-white/40 text-xs w-10 text-right">{DAYS[day].slice(0, 3)}</span>
      <div
        ref={barRef}
        className="flex-1 h-7 bg-white/5 rounded-sm flex cursor-crosshair select-none relative overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {grid.map((cell, idx) => (
          <div
            key={idx}
            className={`flex-1 transition-colors border-r border-white/[0.02] ${cellColor(cell)} ${idx % 2 === 0 ? "border-l-white/[0.06]" : ""}`}
          />
        ))}
        {/* Hour markers — every hour with AM/PM */}
        {Array.from({ length: 24 }, (_, h) => {
          const label = h === 0 ? "12A" : h === 12 ? "12P" : h < 12 ? `${h}A` : `${h - 12}P`;
          return (
            <div
              key={h}
              className={`absolute top-0 bottom-0 border-l pointer-events-none ${h % 6 === 0 ? "border-white/15" : "border-white/[0.04]"}`}
              style={{ left: `${(h / 24) * 100}%` }}
            >
              <span className="absolute -top-0.5 left-0.5 text-[8px] text-white/25 leading-none">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ===== AVAILABILITY SCHEDULER PANEL =====
function AvailabilityScheduler() {
  const [slots, setSlots] = useState([]);
  const [tz, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paintMode, setPaintMode] = useState("adult"); // "adult" | "kid"

  useEffect(() => {
    // Load both availability and user's settings timezone in parallel
    Promise.all([
      axios.get(`${API}/multiplayer/availability`),
      axios.get(`${API}/settings/timezone`).catch(() => ({ data: { timezone: null } })),
    ]).then(([availRes, tzRes]) => {
      const settingsTz = tzRes.data?.timezone;
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const savedTz = availRes.data?.timezone;
      // Priority: previously saved (if non-UTC default) → settings tz → browser tz → UTC
      const effective = (savedTz && savedTz !== "UTC") ? savedTz : (settingsTz && settingsTz !== "UTC") ? settingsTz : (browserTz || "UTC");
      setSlots(availRes.data?.slots || []);
      setTz(effective);
      setEnabled(availRes.data?.enabled !== false);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleDayUpdate = (day, newDaySlots) => {
    setSlots(prev => [...prev.filter(s => s.day !== day), ...newDaySlots]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/multiplayer/availability`, { slots, timezone: tz, enabled });
      toast.success("Availability saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      const r = await axios.patch(`${API}/multiplayer/availability/toggle`);
      setEnabled(r.data.enabled);
      toast.success(r.data.message);
    } catch {
      toast.error("Failed to toggle availability");
    }
  };

  const handleClear = () => {
    setSlots([]);
  };

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-white/20" /></div>;

  return (
    <Card className="bg-[#0a0a0a] border-white/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-sm font-medium flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" />
            Availability Schedule
          </CardTitle>
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggle}
              data-testid="mp-availability-toggle"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                enabled
                  ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                  : "bg-red-500/15 text-red-400 border border-red-500/30"
              }`}
            >
              {enabled ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
              {enabled ? "Active" : "Paused (Vacation)"}
            </button>
          </div>
        </div>
        <p className="text-white/30 text-xs mt-1">Click and drag to set when you're available. Click again to cycle: Available → Flexible → Clear. Pick a paint mode below to set kid-friendly vs adult hours.</p>
        <p className="text-white/40 text-[11px] mt-1 flex items-center gap-1">
          <Globe className="w-3 h-3" />
          Showing in <span className="text-white/70 font-medium" data-testid="mp-schedule-tz">{tz}</span>
          <span className="text-white/25">— change your timezone in Settings</span>
        </p>
      </CardHeader>
      <CardContent className={`space-y-1.5 ${!enabled ? "opacity-40 pointer-events-none" : ""}`}>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-white/40 text-xs mr-1">Paint:</span>
          <button
            data-testid="mp-paint-mode-kid"
            onClick={() => setPaintMode("kid")}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
              paintMode === "kid"
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                : "bg-white/5 text-white/50 border-white/10 hover:text-white/70"
            }`}
          >
            <span className="w-2 h-2 rounded-sm bg-emerald-500/80" />
            Kid-Friendly
          </button>
          <button
            data-testid="mp-paint-mode-adult"
            onClick={() => setPaintMode("adult")}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
              paintMode === "adult"
                ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                : "bg-white/5 text-white/50 border-white/10 hover:text-white/70"
            }`}
          >
            <span className="w-2 h-2 rounded-sm bg-amber-500/80" />
            Adult Only
          </button>
          <div className="ml-auto flex items-center gap-3 text-[10px] text-white/40 flex-wrap">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500/70" /> Kid · Available</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-500/70" /> Adult · Available</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-sky-500/55" /> Kid · Flexible</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-orange-500/55" /> Adult · Flexible</span>
          </div>
        </div>
        {DAYS.map((_, day) => (
          <AvailabilityBar
            key={day}
            day={day}
            paintMode={paintMode}
            daySlots={slots.filter(s => s.day === day)}
            onUpdate={(newSlots) => handleDayUpdate(day, newSlots)}
          />
        ))}
        <div className="flex items-center justify-between pt-3">
          <Button variant="ghost" size="sm" className="text-white/30 hover:text-white/60 text-xs" onClick={handleClear}>
            Clear All
          </Button>
          <Button
            data-testid="mp-save-availability"
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Clock className="w-3.5 h-3.5 mr-1.5" />}
            Save Schedule
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ===== ADD GAME DIALOG =====
function AddGameDialog({ open, onOpenChange, onAdded }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showGamertag, setShowGamertag] = useState(false);
  const [kidSafe, setKidSafe] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allowedMatchTypes, setAllowedMatchTypes] = useState(null);

  const requestKidSafe = (next) => {
    if (next && shouldShowKidSafeWarning(selected?.ratings)) {
      setWarnOpen(true);
    } else {
      setKidSafe(next);
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/multiplayer/eligible-games`).then(r => {
      setGames(r.data);
    }).catch(() => toast.error("Failed to load eligible games")).finally(() => setLoading(false));
  }, [open]);

  // When a game is selected, fetch its allowed match types
  useEffect(() => {
    if (!selected?.masterGameId) { setAllowedMatchTypes(null); return; }
    axios.get(`${API}/multiplayer/games/${selected.masterGameId}/allowed-categories`)
      .then(r => setAllowedMatchTypes(r.data.has_constraints ? new Set(r.data.allowed_match_types) : null))
      .catch(() => setAllowedMatchTypes(null));
  }, [selected]);

  const isCategoryDisabled = (key) => {
    const cat = CATEGORIES[key];
    if (!cat?.isMatchType) return false;
    if (!allowedMatchTypes) return false;
    return !allowedMatchTypes.has(key);
  };

  const filtered = games.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase()) && !g.has_listing
  );

  const toggleCategory = (cat) => {
    if (cat === "not_interested") {
      setCategories(["not_interested"]);
      return;
    }
    if (isCategoryDisabled(cat)) return;
    setCategories(prev => {
      const without = prev.filter(c => c !== "not_interested");
      return without.includes(cat) ? without.filter(c => c !== cat) : [...without, cat];
    });
  };

  const handleSave = async () => {
    if (!selected) return;
    if (categories.length === 0) { toast.error("Select at least one category"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/multiplayer/listings`, {
        game_id: selected.id,
        categories,
        show_gamertag: showGamertag,
        kid_safe: kidSafe,
      });
      toast.success("Game added to multiplayer listings");
      onAdded?.();
      onOpenChange(false);
      setSelected(null);
      setCategories([]);
      setShowGamertag(false);
      setKidSafe(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add listing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Add Game to Multiplayer</DialogTitle>
        </DialogHeader>

        {!selected ? (
          <div className="space-y-3">
            <Input
              data-testid="mp-game-search"
              placeholder="Search your multiplayer games..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
            />
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 space-y-2">
                <Gamepad2 className="w-8 h-8 text-white/10 mx-auto" />
                <p className="text-white/40 text-sm">
                  {games.length === 0 ? "No multiplayer-eligible games in your library." : "No matching games found."}
                </p>
                {games.length === 0 && (
                  <p className="text-white/25 text-xs">Games must be marked as multiplayer in the Master Database and linked to your library.</p>
                )}
              </div>
            ) : (
              <div className="space-y-1 max-h-[50vh] overflow-y-auto">
                {filtered.map(g => (
                  <button
                    key={g.id}
                    data-testid={`mp-game-${g.id}`}
                    onClick={() => setSelected(g)}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors text-left"
                  >
                    {g.boxArtUrl ? (
                      <img src={g.boxArtUrl} alt="" className="w-10 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-10 h-12 bg-white/5 rounded flex items-center justify-center">
                        <Gamepad2 className="w-4 h-4 text-white/20" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{g.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <PlatformBadge platform={g.platform} size="xs" showLabel={false} />
                        {g.systems?.[0] && <span className="text-white/30 text-xs">{g.systems[0]}</span>}
                        {g.multiplayer_type && <MpTypeBadge type={g.multiplayer_type} />}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              {selected.boxArtUrl ? (
                <img src={selected.boxArtUrl} alt="" className="w-12 h-14 object-cover rounded" />
              ) : (
                <div className="w-12 h-14 bg-white/10 rounded flex items-center justify-center">
                  <Gamepad2 className="w-5 h-5 text-white/20" />
                </div>
              )}
              <div>
                <p className="text-white font-medium">{selected.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <PlatformBadge platform={selected.platform} size="xs" showLabel={false} />
                  {selected.systems?.[0] && <span className="text-white/30 text-xs">{selected.systems[0]}</span>}
                  {selected.multiplayer_type && <MpTypeBadge type={selected.multiplayer_type} />}
                </div>
              </div>
              <Button variant="ghost" size="sm" className="ml-auto text-white/40 hover:text-white" onClick={() => setSelected(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div>
              <p className="text-white/60 text-sm mb-2">What are you looking to do?</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(CATEGORIES).map(([key, cat]) => {
                  const Icon = cat.icon;
                  const active = categories.includes(key);
                  const disabled = isCategoryDisabled(key);
                  return (
                    <button
                      key={key}
                      data-testid={`mp-cat-${key}`}
                      onClick={() => toggleCategory(key)}
                      disabled={disabled}
                      title={disabled ? "Not supported by this game" : undefined}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-all ${
                        disabled ? "opacity-30 cursor-not-allowed border-white/5 text-white/40"
                        : active ? cat.bg + " border-current" : "border-white/10 text-white/50 hover:border-white/20"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {cat.label}
                    </button>
                  );
                })}
              </div>
              {allowedMatchTypes && (
                <p className="text-white/30 text-[11px] mt-1.5">Disabled match types aren't supported by this game (per MDB).</p>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-2">
                <Baby className={`w-4 h-4 ${kidSafe ? "text-emerald-400" : "text-white/40"}`} />
                <div>
                  <span className="text-white/70 text-sm block">OK to play during my Kid-Friendly hours</span>
                  <span className="text-white/30 text-[11px]">For games safe to play around children</span>
                </div>
              </div>
              <Switch data-testid="mp-add-kidsafe" checked={kidSafe} onCheckedChange={requestKidSafe} />
            </div>

            <KidSafeMatureWarningDialog
              open={warnOpen}
              gameName={selected?.name}
              ratings={selected?.ratings}
              onConfirm={() => { setKidSafe(true); setWarnOpen(false); }}
              onCancel={() => setWarnOpen(false)}
            />

            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-2">
                {showGamertag ? <Eye className="w-4 h-4 text-emerald-400" /> : <EyeOff className="w-4 h-4 text-white/40" />}
                <span className="text-white/70 text-sm">Show my gamertag to other players</span>
              </div>
              <Switch checked={showGamertag} onCheckedChange={setShowGamertag} />
            </div>

            <Button
              data-testid="mp-save-listing"
              onClick={handleSave}
              disabled={categories.length === 0 || saving}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add to Multiplayer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ===== EDIT LISTING DIALOG (inline categories edit) =====
function EditListingDialog({ open, onOpenChange, listing, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [showGamertag, setShowGamertag] = useState(false);
  const [kidSafe, setKidSafe] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allowedMatchTypes, setAllowedMatchTypes] = useState(null); // null = unknown / no constraints loaded

  const requestKidSafe = (next) => {
    if (next && shouldShowKidSafeWarning(listing?.ratings)) {
      setWarnOpen(true);
    } else {
      setKidSafe(next);
    }
  };

  useEffect(() => {
    if (listing) {
      setCategories(listing.categories || []);
      setShowGamertag(!!listing.show_gamertag);
      setKidSafe(!!listing.kid_safe);
      // Load allowed match types for this game
      if (listing.master_game_id) {
        axios.get(`${API}/multiplayer/games/${listing.master_game_id}/allowed-categories`)
          .then(r => setAllowedMatchTypes(r.data.has_constraints ? new Set(r.data.allowed_match_types) : null))
          .catch(() => setAllowedMatchTypes(null));
      } else {
        setAllowedMatchTypes(null);
      }
    }
  }, [listing]);

  const isCategoryDisabled = (key) => {
    const cat = CATEGORIES[key];
    if (!cat?.isMatchType) return false; // purpose categories always allowed
    if (!allowedMatchTypes) return false; // no constraint set on MDB
    return !allowedMatchTypes.has(key);
  };

  const toggleCategory = (cat) => {
    if (cat === "not_interested") return; // not editable here; user should delete instead
    if (isCategoryDisabled(cat)) return;
    setCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const handleSave = async () => {
    if (categories.length === 0) { toast.error("Select at least one category"); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/multiplayer/listings/${listing.id}`, {
        categories,
        show_gamertag: showGamertag,
        kid_safe: kidSafe,
      });
      toast.success("Listing updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (!listing) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {listing.box_art_url && <img src={listing.box_art_url} alt="" className="w-8 h-10 object-cover rounded" />}
            Edit: {listing.game_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-white/60 text-sm mb-2">What are you looking to do?</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CATEGORIES).map(([key, cat]) => {
                const Icon = cat.icon;
                const active = categories.includes(key);
                const disabled = isCategoryDisabled(key);
                return (
                  <button
                    key={key}
                    data-testid={`mp-edit-cat-${key}`}
                    onClick={() => toggleCategory(key)}
                    disabled={disabled}
                    title={disabled ? "Not supported by this game" : undefined}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-all ${
                      disabled ? "opacity-30 cursor-not-allowed border-white/5 text-white/40"
                      : active ? cat.bg + " border-current" : "border-white/10 text-white/50 hover:border-white/20"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.label}
                  </button>
                );
              })}
            </div>
            {allowedMatchTypes && (
              <p className="text-white/30 text-[11px] mt-1.5">Disabled match types aren't supported by this game (per MDB).</p>
            )}
          </div>
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <div className="flex items-center gap-2">
              <Baby className={`w-4 h-4 ${kidSafe ? "text-emerald-400" : "text-white/40"}`} />
              <div>
                <span className="text-white/70 text-sm block">OK to play during my Kid-Friendly hours</span>
                <span className="text-white/30 text-[11px]">For games safe to play around children (no profanity, no mature content)</span>
              </div>
            </div>
            <Switch data-testid="mp-edit-kidsafe" checked={kidSafe} onCheckedChange={requestKidSafe} />
          </div>
          <KidSafeMatureWarningDialog
            open={warnOpen}
            gameName={listing?.game_name}
            ratings={listing?.ratings}
            onConfirm={() => { setKidSafe(true); setWarnOpen(false); }}
            onCancel={() => setWarnOpen(false)}
          />
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <div className="flex items-center gap-2">
              {showGamertag ? <Eye className="w-4 h-4 text-emerald-400" /> : <EyeOff className="w-4 h-4 text-white/40" />}
              <span className="text-white/70 text-sm">Show my gamertag to other players</span>
            </div>
            <Switch checked={showGamertag} onCheckedChange={setShowGamertag} />
          </div>
          <Button
            data-testid="mp-edit-save"
            onClick={handleSave}
            disabled={categories.length === 0 || saving}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ===== GAMERTAGS MANAGER DIALOG =====
const SESSION_SITES = {
  ta: { label: "TrueAchievements", short: "TA", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  tt: { label: "TrueTrophies", short: "TT", color: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  tsa: { label: "TrueSteamAchievements", short: "TSA", color: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
  exophase: { label: "Exophase", short: "Exo", color: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30" },
  discord: { label: "Discord", short: "Discord", color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
  playstation_party: { label: "PlayStation Party", short: "PS Party", color: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  xbox_party: { label: "Xbox Party", short: "Xbox Party", color: "bg-green-500/15 text-green-300 border-green-500/30" },
};

function GamertagsDialog({ open, onOpenChange }) {
  const [gamertags, setGamertags] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [sessionSites, setSessionSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/multiplayer/gamertags`),
      axios.get(`${API}/platforms`),
      axios.get(`${API}/multiplayer/session-sites`),
    ]).then(([gtRes, pRes, ssRes]) => {
      setPlatforms(pRes.data || []);
      const existing = gtRes.data || [];
      const all = (pRes.data || []).map(p => {
        const found = existing.find(g => g.platform === p.id);
        return { platform: p.id, gamertag: found?.gamertag || "" };
      });
      setGamertags(all);
      setSessionSites(ssRes.data?.session_sites || []);
    }).catch(() => toast.error("Failed to load gamertags")).finally(() => setLoading(false));
  }, [open]);

  const handleChange = (platform, value) => {
    setGamertags(prev => prev.map(g => g.platform === platform ? { ...g, gamertag: value } : g));
  };

  const toggleSite = (site) => {
    setSessionSites(prev => prev.includes(site) ? prev.filter(s => s !== site) : [...prev, site]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const filtered = gamertags.filter(g => g.gamertag.trim());
      await Promise.all([
        axios.put(`${API}/multiplayer/gamertags`, filtered),
        axios.put(`${API}/multiplayer/session-sites`, { session_sites: sessionSites }),
      ]);
      toast.success("Saved");
      onOpenChange(false);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const platformName = (id) => platforms.find(p => p.id === id)?.name || id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <IdCard className="w-5 h-5 text-blue-400" />
            Gamertags & Session Sites
          </DialogTitle>
        </DialogHeader>
        <p className="text-white/40 text-xs">Gamertags are stored per platform. Session sites tell other players where you're willing to coordinate matches.</p>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2 font-medium">Platform Gamertags</p>
              <div className="space-y-2">
                {gamertags.map(g => (
                  <div key={g.platform} className="flex items-center gap-2">
                    <div className="w-24 flex items-center gap-1.5 shrink-0">
                      <PlatformBadge platform={g.platform} size="xs" showLabel={false} />
                      <span className="text-white/60 text-xs truncate">{platformName(g.platform)}</span>
                    </div>
                    <Input
                      data-testid={`mp-gamertag-${g.platform}`}
                      placeholder={`${platformName(g.platform)} gamertag...`}
                      value={g.gamertag}
                      onChange={e => handleChange(g.platform, e.target.value)}
                      className="bg-white/5 border-white/10 text-white text-sm h-8 flex-1"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2 font-medium">Session Coordination Sites</p>
              <p className="text-white/30 text-[11px] mb-2">Where you're OK setting up sessions (e.g., TA Sessions).</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(SESSION_SITES).map(([key, s]) => {
                  const active = sessionSites.includes(key);
                  return (
                    <button
                      key={key}
                      data-testid={`mp-session-site-${key}`}
                      onClick={() => toggleSite(key)}
                      className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                        active ? s.color : "bg-white/5 text-white/40 border-white/10 hover:text-white/60"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              data-testid="mp-gamertags-save"
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 w-full mt-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ===== REPORT PLAYER DIALOG =====
function ReportDialog({ open, onOpenChange, target }) {
  const [reasons, setReasons] = useState({});
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setDetails("");
    setScreenshotUrl("");
    axios.get(`${API}/multiplayer/report-reasons`).then(r => setReasons(r.data)).catch(() => {});
  }, [open]);

  const handleScreenshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Get signed upload params
      const sigRes = await axios.get(`${API}/cloudinary/signature`, { params: { folder: "gamepop/reports" } });
      const { signature, timestamp, api_key, cloud_name, folder, transformation } = sigRes.data;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", api_key);
      fd.append("timestamp", timestamp);
      fd.append("signature", signature);
      fd.append("folder", folder);
      if (transformation) fd.append("transformation", transformation);
      const upRes = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, { method: "POST", body: fd });
      const upJson = await upRes.json();
      if (!upJson.secure_url) throw new Error(upJson.error?.message || "Upload failed");
      setScreenshotUrl(upJson.secure_url);
      toast.success("Screenshot attached");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    if (details.trim().length < 5) { toast.error("Please add details (min 5 chars)"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/multiplayer/reports`, {
        reported_user_id: target.user_id,
        master_game_id: target.master_game_id,
        reason,
        details: details.trim(),
        screenshot_url: screenshotUrl || null,
      });
      toast.success(`Report submitted. Active marks on ${target.user_name}: ${res.data.reported_user_status.mark_count}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!target) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-400" />
            Report: {target.user_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-white/40 text-xs">Marks expire after 7 days. 3 active marks = visible badge; 5 active marks = auto-block pending admin review. False reports may be dismissed.</p>
          <div>
            <p className="text-white/60 text-sm mb-1.5">Reason</p>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="mp-report-reason" className="bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(reasons).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-white/60 text-sm mb-1.5">Details</p>
            <Textarea
              data-testid="mp-report-details"
              placeholder="What happened? Be specific."
              value={details}
              onChange={e => setDetails(e.target.value)}
              rows={4}
              className="bg-white/5 border-white/10 text-white text-sm"
            />
          </div>
          <div>
            <p className="text-white/60 text-sm mb-1.5">Screenshot (optional)</p>
            {screenshotUrl ? (
              <div className="flex items-center gap-2">
                <img src={screenshotUrl} alt="" className="w-16 h-16 object-cover rounded border border-white/10" />
                <button onClick={() => setScreenshotUrl("")} className="text-white/40 hover:text-red-400 text-xs">Remove</button>
              </div>
            ) : (
              <label className={`flex items-center gap-2 px-3 py-2 rounded border border-dashed border-white/15 text-white/50 text-xs cursor-pointer hover:border-white/30 ${uploading ? "opacity-50" : ""}`}>
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                {uploading ? "Uploading..." : "Attach screenshot"}
                <input data-testid="mp-report-screenshot" type="file" accept="image/*" className="hidden" onChange={handleScreenshot} disabled={uploading} />
              </label>
            )}
          </div>
          <Button
            data-testid="mp-report-submit"
            onClick={handleSubmit}
            disabled={submitting || !reason || details.trim().length < 5}
            className="w-full bg-red-600 hover:bg-red-700"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
            Submit Report
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ===== GAME PLAYERS DIALOG =====
function GamePlayersDialog({ open, onOpenChange, masterGameId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [posting, setPosting] = useState(false);
  const [tab, setTab] = useState("players");
  const [reportTarget, setReportTarget] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    if (!open || !masterGameId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/multiplayer/games/${masterGameId}/players`),
      axios.get(`${API}/multiplayer/billboard/${masterGameId}`),
    ]).then(([playersRes, msgRes]) => {
      setData(playersRes.data);
      setMessages(msgRes.data);
    }).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [open, masterGameId]);

  const handlePost = async () => {
    if (!newMessage.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/multiplayer/billboard`, {
        master_game_id: masterGameId,
        message: newMessage.trim(),
      });
      setMessages(prev => [res.data, ...prev]);
      setNewMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally {
      setPosting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 flex-wrap">
            {data?.game?.boxArtUrl && <img src={data.game.boxArtUrl} alt="" className="w-8 h-10 object-cover rounded" />}
            <span>{data?.game?.name || "Players"}</span>
            {data?.game?.crossPlatform && (
              <span
                data-testid="mp-crossplatform-badge"
                className="ml-1 flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30"
                title={
                  (data?.linked_games || []).length
                    ? `Cross-play — also includes: ${data.linked_games.map(g => g.name).join(", ")}`
                    : "Cross-platform play — players from all platforms share this billboard"
                }
              >
                <Globe className="w-3 h-3" /> Cross-Platform
                {(data?.linked_games || []).length ? ` ×${1 + data.linked_games.length}` : ""}
              </span>
            )}
            {data?.game && (
              <button
                data-testid="mp-suggest-crossplay"
                onClick={() => setSuggestOpen(true)}
                className="ml-auto text-[10px] text-white/40 hover:text-violet-300 flex items-center gap-1 transition-colors"
                title="Suggest this game should share players with another platform's version"
              >
                <Globe className="w-3 h-3" />
                {data?.game?.crossPlatform ? "Suggest another link" : "Suggest cross-play link"}
              </button>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-1 p-1 bg-white/5 rounded-lg">
              <button
                onClick={() => setTab("players")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm transition-colors ${tab === "players" ? "bg-white/10 text-white" : "text-white/40 hover:text-white/60"}`}
              >
                <Users className="w-4 h-4" /> Players ({data?.players?.length || 0})
              </button>
              <button
                onClick={() => setTab("billboard")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm transition-colors ${tab === "billboard" ? "bg-white/10 text-white" : "text-white/40 hover:text-white/60"}`}
              >
                <MessageSquare className="w-4 h-4" /> Billboard ({messages.length})
              </button>
            </div>

            {tab === "players" ? (
              data?.players?.length > 0 ? (
                <div className="space-y-2">
                  {data.players.map(p => (
                    <div key={p.id} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/50 text-xs font-bold shrink-0">
                        {p.user_name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-medium">{p.user_name}</span>
                          {p.has_mark_badge && (
                            <span
                              data-testid={`mp-mark-badge-${p.id}`}
                              className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30"
                              title={`Has ${p.mark_count} active moderation marks`}
                            >
                              <Shield className="w-3 h-3" /> Marks: {p.mark_count}
                            </span>
                          )}
                          {p.parental_mode && (
                            <span
                              data-testid={`mp-parental-badge-${p.id}`}
                              className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                              title="In Parental Mode right now — keep it clean"
                            >
                              <Baby className="w-3 h-3" /> Parental
                            </span>
                          )}
                          {p.gamertag && (
                            <span className="text-emerald-400 text-xs bg-emerald-500/10 px-1.5 py-0.5 rounded">{p.gamertag}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {p.categories?.map(cat => <CategoryBadge key={cat} category={cat} size="xs" />)}
                        </div>
                        {p.session_sites?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {p.session_sites.map(s => {
                              const site = SESSION_SITES[s];
                              if (!site) return null;
                              return (
                                <span key={s} className={`text-[10px] px-1.5 py-0.5 rounded border ${site.color}`} title={site.label}>
                                  {site.short}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <PlatformBadge platform={p.platform} size="xs" showLabel={false} />
                        <button
                          data-testid={`mp-report-${p.id}`}
                          onClick={() => setReportTarget({ user_id: p.user_id, user_name: p.user_name, master_game_id: masterGameId })}
                          className="text-[10px] text-white/30 hover:text-red-400 flex items-center gap-0.5 transition-colors"
                          title="Report this player"
                        >
                          <Shield className="w-3 h-3" /> Report
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/40 text-sm text-center py-6">No other players have listed this game yet.</p>
              )
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Textarea
                    data-testid="mp-billboard-input"
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    placeholder="Post a message about this game..."
                    className="bg-white/5 border-white/10 text-white text-sm min-h-[60px] resize-none"
                    maxLength={500}
                  />
                  <Button
                    data-testid="mp-billboard-send"
                    onClick={handlePost}
                    disabled={!newMessage.trim() || posting}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 self-end"
                  >
                    {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                {messages.length > 0 ? (
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {messages.map(m => (
                      <div key={m.id} className="p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-sm font-medium">{m.user_name}</span>
                          <span className="text-white/30 text-xs">{new Date(m.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-white/70 text-sm">{m.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm text-center py-4">No messages yet. Start the conversation!</p>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
    <ReportDialog
      open={!!reportTarget}
      onOpenChange={(o) => !o && setReportTarget(null)}
      target={reportTarget}
    />
    <CrossPlaySuggestionDialog
      open={suggestOpen}
      onOpenChange={setSuggestOpen}
      sourceGame={data?.game ? { id: masterGameId, name: data.game.name } : null}
    />
    </>
  );
}


// ===== MAIN MULTIPLAYER PAGE =====
export default function Multiplayer() {
  const [myListings, setMyListings] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [gamertagsOpen, setGamertagsOpen] = useState(false);
  const [playersDialogId, setPlayersDialogId] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [parentalOnly, setParentalOnly] = useState(false);
  const [tab, setTab] = useState("my-games");
  const [mpStatus, setMpStatus] = useState(null);
  // My Listings — sort + filter
  const [myListingsSort, setMyListingsSort] = useState("recent");
  const [myListingsCatFilter, setMyListingsCatFilter] = useState("all");
  const [myListingsPlatformFilter, setMyListingsPlatformFilter] = useState("all");
  const [myListingsKidOnly, setMyListingsKidOnly] = useState(false);
  const [myListingsCrossOnly, setMyListingsCrossOnly] = useState(false);

  // Availability nudge — prompt players who haven't set a schedule (toggleable)
  const [showAvailabilityNudge, setShowAvailabilityNudge] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const [av, prefs] = await Promise.all([
          axios.get(`${API}/multiplayer/availability`).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/preferences`).catch(() => ({ data: {} })),
        ]);
        const hasSchedule = Array.isArray(av.data?.slots) && av.data.slots.length > 0;
        const remindersOn = prefs.data?.multiplayerAvailabilityReminders !== false;
        setShowAvailabilityNudge(!hasSchedule && remindersOn);
      } catch { /* ignore */ }
    })();
  }, []);

  const dismissAvailabilityNudge = async (turnOff) => {
    setShowAvailabilityNudge(false);
    if (turnOff) {
      try {
        await axios.put(`${API}/settings/preferences`, { multiplayerAvailabilityReminders: false });
        toast.success("Availability reminders turned off — manage this in Settings");
      } catch { /* ignore */ }
    }
  };

  const scrollToAvailability = () => {
    document.getElementById("mp-availability-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const fetchData = useCallback(async () => {
    try {
      const [listingsRes, discoverRes, statusRes] = await Promise.all([
        axios.get(`${API}/multiplayer/my-listings`),
        axios.get(`${API}/multiplayer/discover-with-schedule`, { params: parentalOnly ? { parental_only: true } : {} }),
        axios.get(`${API}/multiplayer/user-status`).catch(() => ({ data: null })),
      ]);
      setMyListings(listingsRes.data);
      setDiscover(discoverRes.data);
      setMpStatus(statusRes.data);
    } catch {
      // Silently handle — page still renders with empty state
    } finally {
      setLoading(false);
    }
  }, [parentalOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/multiplayer/listings/${id}`);
      toast.success("Listing removed");
      fetchData();
    } catch {
      toast.error("Failed to remove listing");
    }
  };

  const filteredDiscover = discover.filter(g => {
    if (filterPlatform !== "all" && g.platform !== filterPlatform) return false;
    if (filterCategory !== "all" && !g.category_counts?.[filterCategory]) return false;
    return true;
  });

  const platforms = [...new Set(discover.map(g => g.platform).filter(Boolean))];

  // My Listings — derived list with sorting + filtering
  const myListingsPlatforms = [...new Set(myListings.map(l => l.platform).filter(Boolean))];
  const sortedMyListings = (() => {
    let arr = myListings.filter(l => {
      if (myListingsPlatformFilter !== "all" && l.platform !== myListingsPlatformFilter) return false;
      if (myListingsCatFilter !== "all" && !(l.categories || []).includes(myListingsCatFilter)) return false;
      if (myListingsKidOnly && !l.kid_safe) return false;
      if (myListingsCrossOnly && !l.cross_platform) return false;
      return true;
    });
    const byName = (a, b) => (a.game_name || "").localeCompare(b.game_name || "");
    switch (myListingsSort) {
      case "title":
        arr = [...arr].sort(byName);
        break;
      case "matches":
        arr = [...arr].sort((a, b) => (b.match_count || 0) - (a.match_count || 0) || byName(a, b));
        break;
      case "kid_first":
        arr = [...arr].sort((a, b) => (b.kid_safe ? 1 : 0) - (a.kid_safe ? 1 : 0) || byName(a, b));
        break;
      case "cross_first":
        arr = [...arr].sort((a, b) => (b.cross_platform ? 1 : 0) - (a.cross_platform ? 1 : 0) || byName(a, b));
        break;
      case "platform":
        arr = [...arr].sort((a, b) => (a.platform || "").localeCompare(b.platform || "") || byName(a, b));
        break;
      case "recent":
      default:
        // Backend already returns by updated_at desc — keep that order
        break;
    }
    return arr;
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white/20" />
      </div>
    );
  }

  return (
    <div data-testid="multiplayer-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-[Unbounded]">Multiplayer</h1>
          <p className="text-white/40 text-sm mt-1">Find players, coordinate sessions, and complete games together</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="mp-manage-gamertags-btn"
            onClick={() => setGamertagsOpen(true)}
            variant="outline"
            className="bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10"
          >
            <IdCard className="w-4 h-4 mr-2" /> Gamertags
          </Button>
          <Button
            data-testid="mp-add-game-btn"
            onClick={() => setAddDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Game
          </Button>
        </div>
      </div>

      {/* Moderation status banner */}
      {mpStatus?.blocked && (
        <div data-testid="mp-blocked-banner" className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2">
          <Shield className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-red-300 font-medium text-sm">You're blocked from Multiplayer</p>
            <p className="text-white/60 text-xs mt-0.5">{mpStatus.blocked_reason || "Pending admin review."} Listings and billboard posts are disabled until an admin lifts the block.</p>
          </div>
        </div>
      )}
      {!mpStatus?.blocked && mpStatus?.mark_count > 0 && (
        <div data-testid="mp-marks-banner" className={`rounded-lg border p-3 flex items-start gap-2 ${
          mpStatus.show_mark_badge ? "border-red-500/40 bg-red-500/10" : "border-amber-500/40 bg-amber-500/10"
        }`}>
          <Shield className={`w-5 h-5 mt-0.5 shrink-0 ${mpStatus.show_mark_badge ? "text-red-400" : "text-amber-400"}`} />
          <div className="flex-1">
            <p className={`font-medium text-sm ${mpStatus.show_mark_badge ? "text-red-300" : "text-amber-300"}`}>
              {mpStatus.mark_count} active moderation mark{mpStatus.mark_count > 1 ? "s" : ""}
            </p>
            <p className="text-white/60 text-xs mt-0.5">
              {mpStatus.show_mark_badge
                ? `Your profile now shows a visible mark badge to other players. Reaching ${mpStatus.threshold_block} active marks will block you from multiplayer pending admin review.`
                : `${mpStatus.threshold_badge - mpStatus.mark_count} more mark${mpStatus.threshold_badge - mpStatus.mark_count > 1 ? "s" : ""} will add a visible badge to your profile. Marks expire after 7 days.`}
            </p>
          </div>
        </div>
      )}

      {/* Availability nudge — hidden once a schedule exists or reminders are off */}
      {showAvailabilityNudge && (
        <div data-testid="mp-availability-nudge" className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 p-3 flex items-start gap-3">
          <Calendar className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-cyan-200 font-medium text-sm">Set your availability to start getting matches</p>
            <p className="text-white/60 text-xs mt-0.5">
              Add the times you're usually free so other players on your games can find and schedule sessions with you.
            </p>
            <div className="flex items-center gap-4 mt-2">
              <Button
                size="sm"
                onClick={scrollToAvailability}
                className="bg-cyan-600 hover:bg-cyan-700 h-8"
                data-testid="mp-nudge-set-availability"
              >
                Set availability
              </Button>
              <button
                onClick={() => dismissAvailabilityNudge(true)}
                className="text-white/40 hover:text-white/70 text-xs underline"
                data-testid="mp-nudge-dont-remind"
              >
                Don't remind me
              </button>
            </div>
          </div>
          <button
            onClick={() => dismissAvailabilityNudge(false)}
            className="text-white/30 hover:text-white/60"
            title="Dismiss"
            data-testid="mp-nudge-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Availability Scheduler */}
      <div id="mp-availability-section">
        <AvailabilityScheduler />
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-white/5 rounded-lg w-fit">
        <button
          data-testid="mp-tab-my-games"
          onClick={() => setTab("my-games")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "my-games" ? "bg-blue-600 text-white" : "text-white/40 hover:text-white/60"
          }`}
        >
          <Gamepad2 className="w-4 h-4" /> My Listings ({myListings.length})
        </button>
        <button
          data-testid="mp-tab-discover"
          onClick={() => setTab("discover")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "discover" ? "bg-blue-600 text-white" : "text-white/40 hover:text-white/60"
          }`}
        >
          <Users className="w-4 h-4" /> Discover Players
          {discover.length > 0 && (
            <span className="bg-emerald-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{discover.length}</span>
          )}
        </button>
      </div>

      {/* MY GAMES TAB */}
      {tab === "my-games" && (
        <div className="space-y-3">
          {myListings.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="mp-mylistings-toolbar">
              <Select value={myListingsSort} onValueChange={setMyListingsSort}>
                <SelectTrigger data-testid="mp-mylistings-sort" className="w-[180px] bg-white/5 border-white/10 text-white text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Recently updated</SelectItem>
                  <SelectItem value="title">Title (A–Z)</SelectItem>
                  <SelectItem value="matches">Most matches</SelectItem>
                  <SelectItem value="platform">Platform</SelectItem>
                  <SelectItem value="kid_first">Kid-Friendly first</SelectItem>
                  <SelectItem value="cross_first">Cross-Play first</SelectItem>
                </SelectContent>
              </Select>
              {myListingsPlatforms.length > 1 && (
                <Select value={myListingsPlatformFilter} onValueChange={setMyListingsPlatformFilter}>
                  <SelectTrigger data-testid="mp-mylistings-platform" className="w-[150px] bg-white/5 border-white/10 text-white text-xs h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All platforms</SelectItem>
                    {myListingsPlatforms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select value={myListingsCatFilter} onValueChange={setMyListingsCatFilter}>
                <SelectTrigger data-testid="mp-mylistings-category" className="w-[160px] bg-white/5 border-white/10 text-white text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All match types</SelectItem>
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                data-testid="mp-mylistings-kidonly"
                onClick={() => setMyListingsKidOnly(v => !v)}
                className={`text-xs px-3 h-8 rounded-md border transition-colors ${
                  myListingsKidOnly
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                    : "bg-white/5 text-white/50 border-white/10 hover:text-white/80"
                }`}
              >
                Kid-Friendly
              </button>
              <button
                data-testid="mp-mylistings-crossonly"
                onClick={() => setMyListingsCrossOnly(v => !v)}
                className={`text-xs px-3 h-8 rounded-md border transition-colors ${
                  myListingsCrossOnly
                    ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"
                    : "bg-white/5 text-white/50 border-white/10 hover:text-white/80"
                }`}
              >
                Cross-Play
              </button>
              {(myListingsSort !== "recent" || myListingsPlatformFilter !== "all" || myListingsCatFilter !== "all" || myListingsKidOnly || myListingsCrossOnly) && (
                <button
                  data-testid="mp-mylistings-reset"
                  onClick={() => {
                    setMyListingsSort("recent");
                    setMyListingsPlatformFilter("all");
                    setMyListingsCatFilter("all");
                    setMyListingsKidOnly(false);
                    setMyListingsCrossOnly(false);
                  }}
                  className="text-xs px-2 h-8 text-white/40 hover:text-white/70"
                >
                  Reset
                </button>
              )}
              <span className="text-white/30 text-xs ml-auto">
                {sortedMyListings.length} of {myListings.length}
              </span>
            </div>
          )}
          {myListings.length === 0 ? (
            <Card className="bg-[#0a0a0a] border-white/5">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
                  <Swords className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-white font-medium text-lg mb-2">No Multiplayer Listings Yet</h3>
                <p className="text-white/40 text-sm text-center max-w-md mb-1">
                  Add games from your library to let other players know what you want to play.
                </p>
                <p className="text-white/25 text-xs text-center max-w-md mb-6">
                  Games must be marked as multiplayer in the Master Database to appear here.
                </p>
                <Button onClick={() => setAddDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Game
                </Button>
              </CardContent>
            </Card>
          ) : sortedMyListings.length === 0 ? (
            <Card className="bg-[#0a0a0a] border-white/5">
              <CardContent className="flex flex-col items-center justify-center py-10">
                <p className="text-white/40 text-sm">No listings match the current filters.</p>
                <button
                  onClick={() => {
                    setMyListingsSort("recent");
                    setMyListingsPlatformFilter("all");
                    setMyListingsCatFilter("all");
                    setMyListingsKidOnly(false);
                    setMyListingsCrossOnly(false);
                  }}
                  className="text-xs mt-3 text-blue-400 hover:text-blue-300"
                >
                  Clear filters
                </button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {sortedMyListings.map(listing => (
                <Card key={listing.id} className="bg-[#0f0f0f] border-white/5 hover:border-white/10 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {listing.box_art_url ? (
                        <img src={listing.box_art_url} alt="" className="w-14 h-[4.5rem] object-cover rounded flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-[4.5rem] bg-white/5 rounded flex items-center justify-center flex-shrink-0">
                          <Gamepad2 className="w-5 h-5 text-white/20" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{listing.game_name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <PlatformBadge platform={listing.platform} size="xs" showLabel={false} />
                          {listing.systems?.[0] && <span className="text-white/30 text-xs">{listing.systems[0]}</span>}
                          {listing.cross_platform && (
                            <span title="Cross-Play" className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                              Cross
                            </span>
                          )}
                          {listing.kid_safe && (
                            <span title="Kid-Friendly" className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                              Kid OK
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {listing.categories?.map(cat => <CategoryBadge key={cat} category={cat} size="xs" />)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <button
                          data-testid={`mp-open-players-${listing.id}`}
                          onClick={() => setPlayersDialogId(listing.master_game_id)}
                          title="View players & billboard"
                          className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-colors ${
                            listing.match_count > 0
                              ? "text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20"
                              : "text-white/50 bg-white/5 hover:bg-white/10"
                          }`}
                        >
                          {listing.match_count > 0 ? <UserCheck className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                          {listing.match_count > 0 ? listing.match_count : "View"}
                        </button>
                        <div className="flex items-center gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`mp-edit-listing-${listing.id}`}
                            className="text-white/30 hover:text-blue-400 h-7 w-7 p-0"
                            onClick={() => setEditListing(listing)}
                            title="Edit categories"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-white/30 hover:text-red-400 h-7 w-7 p-0"
                            onClick={() => handleDelete(listing.id)}
                            title="Remove listing"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* DISCOVER TAB */}
      {tab === "discover" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={filterPlatform} onValueChange={setFilterPlatform}>
              <SelectTrigger className="w-[150px] bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                {platforms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(CATEGORIES).map(([key, cat]) => (
                  <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              data-testid="mp-filter-parental"
              onClick={() => setParentalOnly(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                parentalOnly
                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                  : "bg-white/5 text-white/50 border-white/10 hover:text-white/70"
              }`}
              title="Only show players currently in their Kid-Friendly hours"
            >
              <Baby className="w-4 h-4" />
              Parental-friendly only
            </button>
          </div>

          {filteredDiscover.length === 0 ? (
            <Card className="bg-[#0a0a0a] border-white/5">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-white/15" />
                </div>
                <h3 className="text-white/60 font-medium mb-1">No Players Found</h3>
                <p className="text-white/30 text-sm text-center max-w-md">
                  No other players have listed games yet. Add your games and be the first!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredDiscover.map(game => (
                <Card
                  key={game.master_game_id}
                  className="bg-[#0f0f0f] border-white/5 hover:border-white/10 transition-colors cursor-pointer"
                  onClick={() => setPlayersDialogId(game.master_game_id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {game.box_art_url ? (
                        <img src={game.box_art_url} alt="" className="w-14 h-[4.5rem] object-cover rounded flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-[4.5rem] bg-white/5 rounded flex items-center justify-center flex-shrink-0">
                          <Gamepad2 className="w-5 h-5 text-white/20" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{game.game_name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <PlatformBadge platform={game.platform} size="xs" showLabel={false} />
                          {game.systems?.[0] && <span className="text-white/30 text-xs">{game.systems[0]}</span>}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {Object.entries(game.category_counts || {}).map(([cat, count]) => (
                            <CategoryBadge key={cat} category={cat} size="xs" count={count} />
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="flex items-center gap-1 text-white/50 text-xs">
                          <Users className="w-3.5 h-3.5" /> {game.player_count}
                        </span>
                        {game.parental_players > 0 && (
                          <span className="flex items-center gap-1 text-emerald-300 text-[10px] bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                            <Baby className="w-3 h-3" /> {game.parental_players} parental
                          </span>
                        )}
                        {game.schedule_matches > 0 && (
                          <span className="flex items-center gap-1 text-emerald-400 text-[10px] bg-emerald-500/10 px-1.5 py-0.5 rounded">
                            <Clock className="w-3 h-3" /> {game.schedule_matches} available
                          </span>
                        )}
                        {game.user_has_game && (
                          <span className="text-blue-400 text-[10px] bg-blue-500/10 px-1.5 py-0.5 rounded">In Library</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <AddGameDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onAdded={fetchData} />
      <EditListingDialog
        open={!!editListing}
        onOpenChange={(open) => !open && setEditListing(null)}
        listing={editListing}
        onSaved={fetchData}
      />
      <GamertagsDialog open={gamertagsOpen} onOpenChange={setGamertagsOpen} />
      <GamePlayersDialog
        open={!!playersDialogId}
        onOpenChange={(open) => !open && setPlayersDialogId(null)}
        masterGameId={playersDialogId}
      />
    </div>
  );
}
